import { existsSync } from "node:fs";
import { appendFile, mkdir, readFile, writeFile } from "node:fs/promises";
import { join } from "node:path";
import type { ProposalAction } from "./proposer.js";

/**
 * Persistence layer for declined and accepted proposals.
 *
 * Two artifacts live under `<cwd>/data/_proactive-curator/`:
 *
 *  - `declined.json` — keyed by proposal kind + fingerprint. Entries expire
 *    after a configurable number of days (default 30) so a one-time decline
 *    does not silence the same topic forever. The expiry check happens both
 *    at read time (filtered out of the active set) and at write time
 *    (purged from disk).
 *  - `accepted.log` — append-only JSON-lines audit trail. Informational
 *    only — the actual file write is the proposal's `onAccept` side effect;
 *    this lets the operator audit *what was accepted and when*.
 *
 * All operations are functional — no in-memory state — so a process can
 * call any function at any time without coordinating with previous calls.
 * Concurrent writes from the same process are rare in a slash-command
 * workflow; we accept last-write-wins.
 */

const STORE_DIR = "data/_proactive-curator";
const DECLINED_FILE = "declined.json";
const ACCEPTED_LOG = "accepted.log";
const DEFAULT_EXPIRY_DAYS = 30;

export type ProposalKind = "capture-insight" | "create-hub";

export interface DeclinedEntry {
  /** ISO-8601 timestamp of when the user declined. */
  readonly declinedAt: string;
  /** Topic slug at decline time (for the audit trail; not part of the fingerprint). */
  readonly topic: string;
  /** ISO-8601 timestamp after which this entry is eligible for re-evaluation. */
  readonly expiresAt: string;
  /** The action the user declined — `create-file`, `append-section`, etc. */
  readonly actionKind: ProposalAction["kind"];
  /** Target path the declined action would have written/touched (data-relative). */
  readonly targetPath: string;
  /** Hub proposals only — the source-doc cluster that triggered this proposal. */
  readonly sourceDocs?: readonly string[];
}

interface DeclinedFile {
  "capture-insight": Record<string, DeclinedEntry>;
  "create-hub": Record<string, DeclinedEntry>;
}

/**
 * Load the active (un-expired) declined fingerprints. The returned set is
 * what {@link ProposerContext.declinedFingerprints} carries during
 * `evaluate()` so the proposer can do its decline check without filesystem
 * access at evaluation time.
 *
 * Missing or corrupt store files return an empty set rather than throwing —
 * a first-run instance simply has nothing declined.
 */
export async function loadDeclinedFingerprints(
  cwd: string,
  now: Date,
): Promise<ReadonlySet<string>> {
  const parsed = await readDeclinedFile(cwd);
  if (!parsed) return new Set();
  const active = new Set<string>();
  const nowMs = now.getTime();
  for (const kind of ["capture-insight", "create-hub"] as const) {
    const entries = parsed[kind] ?? {};
    for (const [fp, entry] of Object.entries(entries)) {
      if (isActive(entry, nowMs)) active.add(fp);
    }
  }
  return active;
}

/**
 * Persist a decline. Purges expired entries from disk in the same write so
 * the store does not grow unbounded.
 */
export async function recordDecline(
  cwd: string,
  args: {
    readonly kind: ProposalKind;
    readonly fingerprint: string;
    readonly topic: string;
    readonly actionKind: ProposalAction["kind"];
    readonly targetPath: string;
    readonly sourceDocs?: readonly string[];
    readonly now: Date;
    readonly expiryDays?: number;
  },
): Promise<void> {
  await ensureStoreDir(cwd);
  const existing = (await readDeclinedFile(cwd)) ?? emptyDeclinedFile();
  const expiryDays = args.expiryDays ?? DEFAULT_EXPIRY_DAYS;
  const expiresAt = new Date(
    args.now.getTime() + expiryDays * 24 * 60 * 60 * 1000,
  ).toISOString();

  const updated: DeclinedFile = {
    "capture-insight": purgeExpired(existing["capture-insight"], args.now),
    "create-hub": purgeExpired(existing["create-hub"], args.now),
  };
  const entry: DeclinedEntry = {
    declinedAt: args.now.toISOString(),
    topic: args.topic,
    expiresAt,
    actionKind: args.actionKind,
    targetPath: args.targetPath,
    ...(args.sourceDocs ? { sourceDocs: args.sourceDocs } : {}),
  };
  updated[args.kind] = { ...updated[args.kind], [args.fingerprint]: entry };

  await writeFile(
    join(cwd, STORE_DIR, DECLINED_FILE),
    JSON.stringify(updated, null, 2) + "\n",
    "utf8",
  );
}

/**
 * Append an acceptance record to the audit trail. Newline-delimited JSON so
 * the file is grep-friendly and tail-friendly without a parser.
 */
export async function recordAcceptance(
  cwd: string,
  args: {
    readonly kind: ProposalKind;
    readonly fingerprint: string;
    readonly topic: string;
    readonly actionKind: ProposalAction["kind"];
    readonly writtenPath: string;
    readonly now: Date;
  },
): Promise<void> {
  await ensureStoreDir(cwd);
  const line = JSON.stringify({
    acceptedAt: args.now.toISOString(),
    kind: args.kind,
    fingerprint: args.fingerprint,
    topic: args.topic,
    actionKind: args.actionKind,
    writtenPath: args.writtenPath,
  });
  await appendFile(join(cwd, STORE_DIR, ACCEPTED_LOG), line + "\n", "utf8");
}

/**
 * Clear declined entries. Optional `kind` argument scopes the reset to
 * just one surface. Called by `/curate --reset-declined` (sub-phase c).
 * Missing store file is a no-op — nothing to reset.
 */
export async function resetDeclined(
  cwd: string,
  kind?: ProposalKind,
): Promise<void> {
  const file = join(cwd, STORE_DIR, DECLINED_FILE);
  if (!existsSync(file)) return;
  if (kind === undefined) {
    await writeFile(file, JSON.stringify(emptyDeclinedFile(), null, 2) + "\n", "utf8");
    return;
  }
  const parsed = (await readDeclinedFile(cwd)) ?? emptyDeclinedFile();
  parsed[kind] = {};
  await writeFile(file, JSON.stringify(parsed, null, 2) + "\n", "utf8");
}

function isActive(entry: DeclinedEntry, nowMs: number): boolean {
  const expiresMs = new Date(entry.expiresAt).getTime();
  return Number.isFinite(expiresMs) && expiresMs > nowMs;
}

// Fingerprints are file-derived keys parsed from JSON on disk; a crafted file
// could carry `__proto__`/`constructor`/`prototype` keys. Build the accumulator
// with a null prototype and skip those keys — belt-and-suspenders against
// prototype pollution when rebuilding the store.
const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);

function purgeExpired(
  entries: Record<string, DeclinedEntry> | undefined,
  now: Date,
): Record<string, DeclinedEntry> {
  if (!entries) return {};
  const out: Record<string, DeclinedEntry> = Object.create(null) as Record<string, DeclinedEntry>;
  const nowMs = now.getTime();
  for (const [fp, entry] of Object.entries(entries)) {
    if (DANGEROUS_KEYS.has(fp)) continue;
    if (isActive(entry, nowMs)) out[fp] = entry;
  }
  return out;
}

async function readDeclinedFile(cwd: string): Promise<DeclinedFile | null> {
  const file = join(cwd, STORE_DIR, DECLINED_FILE);
  if (!existsSync(file)) return null;
  try {
    const raw = await readFile(file, "utf8");
    const parsed = JSON.parse(raw) as Partial<DeclinedFile>;
    return {
      "capture-insight": parsed["capture-insight"] ?? {},
      "create-hub": parsed["create-hub"] ?? {},
    };
  } catch {
    return null;
  }
}

function emptyDeclinedFile(): DeclinedFile {
  return { "capture-insight": {}, "create-hub": {} };
}

async function ensureStoreDir(cwd: string): Promise<void> {
  await mkdir(join(cwd, STORE_DIR), { recursive: true });
}
